import React, { useEffect, useState } from "react";
import useGetRequest from "../../hooks/useGetRequest";
import { ISettings } from "../../types/settings.type";
import { api } from "../../api/api";
import {
  Alert,
  Button,
  Card,
  DatePicker,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Spin,
  Switch,
} from "antd";
import dayjs from "dayjs";

export default function GlobalSettings() {
  const { data, loading, error, refresh } = useGetRequest("/settings");
  const [settings, setSettings] = useState<ISettings | null>(null);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  useEffect(() => {
    if (data?.settings) {
      setSettings(data.settings);
    }
  }, [data]);

  const updateSettings = async (values: ISettings) => {
    setIsLoading(true);

    try {
      const response = await api.post("/settings", values);

      message.success("Данные успешно обновлены");
      refresh();
      setIsModalVisible(false);
    } catch (error) {
      message.error("Ошибка при обновлении данных.");
    } finally {
      setIsLoading(false);
    }
  };

  const openModal = () => {
    setIsEditing(!!settings);
    setIsModalVisible(true);
  };

  if (error) {
    return <Alert message="Ошибка при загрузке данных" type="error" showIcon />;
  }

  if (loading) {
    return <Spin tip="Загрузка..." />;
  }

  return (
    <div>
      {settings ? (
        <Card title="Общие настройки" style={{ width: 500 }}>
          <p>
            <strong>Юк цена USD:</strong> {settings.yukPriceUSD}
          </p>
          <p>
            <strong>Юк цена TJS:</strong> {settings.yukPriceTJS}
          </p>
          <p>
            <strong>Курс нижний лимит:</strong> {settings.exchangeRateLowLimit}
          </p>
          <p>
            <strong>Курс верхний лимит:</strong> {settings.exchangeRateUpLimit}
          </p>
          <p>
            <strong>Минут лимит:</strong> {settings.minuteLimit}
          </p>
          <p>
            <strong>Нижний лимит кг производства %:</strong>{" "}
            {settings.kgReportLowLimit}
          </p>
          <p>
            <strong>Верхний лимит кг производства %:</strong>{" "}
            {settings.kgReportUpLimit}
          </p>
          <p>
            <strong>Нижний лимит кг заказ %:</strong> {settings.kgOrderLowLimit}
          </p>
          <p>
            <strong>Верхний лимит кг заказ %:</strong> {settings.kgOrderUpLimit}
          </p>
          <p>
            <strong>Дата лимит:</strong> {settings.dateLimit ? new Date(settings.dateLimit).toLocaleDateString() : 'Не указана'}
          </p>
          <p>
            <strong>Макс имя филлера:</strong> {settings.maxFillerName || 'Не указано'}
          </p>
          <p>
            <strong>Лимит продаж цены:</strong> {settings.salesPriceLimit ? 'Включен' : 'Отключен'}
          </p>
          <Button type="primary" onClick={openModal} style={{ marginTop: 16 }}>
            Изменить
          </Button>
        </Card>
      ) : (
        <Card title="Настройки не найдены" style={{ width: 500 }}>
          <Button type="primary" onClick={openModal}>
            Добавить
          </Button>
        </Card>
      )}

      <Modal
        title={isEditing ? "Редактировать" : "Добавить"}
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
      >
        <Spin spinning={isLoading}>
          <Form
            initialValues={{
              ...settings,
              dateLimit: settings?.dateLimit ? dayjs(settings.dateLimit) : null
            }}
            onFinish={(values) => {
              const formattedValues = {
                ...values,
                dateLimit: values.dateLimit ? values.dateLimit.toDate() : null
              };
              updateSettings(formattedValues);
            }}
            layout="vertical"
            style={{ maxWidth: 500 }}
          >
            <Form.Item
              label="Цена Юк USD"
              name="yukPriceUSD"
              rules={[
                { required: true, message: "Пожалуйста, введите цена юк!" },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Цена Юк TJS"
              name="yukPriceTJS"
              rules={[
                { required: true, message: "Пожалуйста, введите цена юк!" },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Курс нижний лимит"
              name="exchangeRateLowLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите курс нижний лимит!",
                },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Юк лимит"
              name="yukLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите юк лимит!",
                },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Курс верхний лимит"
              name="exchangeRateUpLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите курс верхний лимит!",
                },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Лимит минут"
              name="minuteLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите минут лимит !",
                },
              ]}
            >
              <InputNumber />
            </Form.Item>

            <Form.Item
              label="Нижний лимит кг производства %"
              name="kgReportLowLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите кг лимит !",
                },
              ]}
            >
              <InputNumber max={100} />
            </Form.Item>

            <Form.Item
              label="Верхний лимит кг производства %"
              name="kgReportUpLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите кг лимит !",
                },
              ]}
            >
              <InputNumber max={100} />
            </Form.Item>

            <Form.Item
              label="Нижний лимит кг заказа %"
              name="kgOrderLowLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите кг лимит !",
                },
              ]}
            >
              <InputNumber max={100} />
            </Form.Item>

            <Form.Item
              label="Верхний лимит кг заказа %"
              name="kgOrderUpLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите кг лимит !",
                },
              ]}
            >
              <InputNumber max={100} />
            </Form.Item>

            <Form.Item
              label="Дата лимит"
              name="dateLimit"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, выберите дату лимит!",
                },
              ]}
            >
              <DatePicker style={{ width: '100%' }} />
            </Form.Item>

            <Form.Item
              label="Макс имя филлера"
              name="maxFillerName"
              rules={[
                {
                  required: true,
                  message: "Пожалуйста, введите макс имя филлера!",
                },
              ]}
            >
              <Input />
            </Form.Item>

            <Form.Item
              label="Лимит продаж цены"
              name="salesPriceLimit"
              valuePropName="checked"
              tooltip="Если включено, нельзя создать заказ с ценой меньше минимальной (кроме админа)"
            >
              <Switch />
            </Form.Item>

            <Button type="primary" htmlType="submit" loading={isLoading}>
              {isEditing ? "Обновить" : "Добавить"}
            </Button>
          </Form>
        </Spin>
      </Modal>
    </div>
  );
}
